import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";
import { CopyComboGrid } from "@/components/CopyComboGrid";
import { kaomojiCollections, getKaomojiCollection } from "@/data/kaomoji";

export const Route = createFileRoute("/kaomoji/$slug")({
  loader: ({ params }) => {
    const col = getKaomojiCollection(params.slug);
    if (!col) throw notFound();
    return { col };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { col } = loaderData;
    const title = `${col.name} kaomoji — copy & paste, ${col.faces.length} text faces`;
    return {
      meta: [
        { title },
        { name: "description", content: col.tagline },
        { property: "og:title", content: `${col.name} kaomoji` },
        { property: "og:description", content: col.tagline },
        { property: "og:url", content: abs(`/kaomoji/${params.slug}`) },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: abs(`/kaomoji/${params.slug}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${col.name} kaomoji`,
            description: col.tagline,
            url: `/kaomoji/${params.slug}`,
            numberOfItems: col.faces.length,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
              { "@type": "ListItem", position: 2, name: "Kaomoji", item: abs("/kaomoji") },
              { "@type": "ListItem", position: 3, name: col.name, item: abs(`/kaomoji/${params.slug}`) },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-12 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Kaomoji set not found</h1>
          <Link to="/kaomoji" className="mt-4 inline-block text-accent hover:underline">
            ← Browse all kaomoji
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: KaomojiPage,
});

function KaomojiPage() {
  const { col } = Route.useLoaderData();
  const others = kaomojiCollections.filter((c) => c.slug !== col.slug);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/kaomoji" className="hover:text-foreground">Kaomoji</Link>
            <span>/</span>
            <span className="text-foreground">{col.name}</span>
          </nav>

          <h1 className="font-display text-3xl font-bold sm:text-5xl">{col.name}</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">{col.tagline}</p>

          <div className="mt-5 max-w-3xl space-y-4">
            {col.intro.map((p, i) => (
              <p key={i} className="text-[15px] leading-relaxed text-foreground/90">{p}</p>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">Add your text to any face ({col.faces.length})</h2>
          <CopyComboGrid combos={col.faces} source="kaomoji" layout="face" wrapText wrapNoun="face" />
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">More kaomoji moods</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((c) => (
              <Link
                key={c.slug}
                to="/kaomoji/$slug"
                params={{ slug: c.slug }}
                className="glass flex items-center gap-3 rounded-xl p-4 transition-colors hover:border-primary/40"
              >
                <span className="min-w-0 shrink-0 max-w-[45%] truncate font-sans text-base text-foreground/90">{c.hero}</span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{c.tagline}</span>
                </span>
                <span className="text-muted-foreground">→</span>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
